import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LogoAtivaJa from "@/components/LogoAtivaJa";
import AlertaNFBloqueante from "@/components/AlertaNFBloqueante";
import { AvaliacaoTrimestral } from "@/components/AvaliacaoTrimestral";
import NovaOfertaSound from "@/components/NovaOfertaSound";
import CadastroEmAnalise from "@/components/CadastroEmAnalise";
import {
  LayoutDashboard, Calendar, Briefcase, Receipt, Settings,
  FileSignature, Megaphone, Wrench, Wallet,
  LogOut, ChevronDown, BellRing, Search, Menu, X
} from "lucide-react";

const NAV = [
  { group: "Painel", items: [
    { to: "/montador", label: "Meu Painel", icon: LayoutDashboard, end: true },
    { to: "/montador/agenda", label: "Minha Agenda", icon: Calendar },
  ]},
  { group: "Fiscal & Financeiro", items: [
    { to: "/montador/financeiro", label: "Meu Financeiro", icon: Wallet },
    { to: "/montador/notas-fiscais", label: "Notas Fiscais", icon: Receipt },
    { to: "/montador/faturas", label: "Minhas Faturas", icon: Receipt },
    { to: "/montador/valores-empresas", label: "Valores das Empresas", icon: Receipt },
    { to: "/montador/perfil-fiscal", label: "Perfil Fiscal MEI", icon: FileSignature },
  ]},
  { group: "Autonomia", items: [
    { to: "/montador/configuracoes", label: "Configurações Operacionais", icon: Settings },
  ]},
  { group: "Comunicação", items: [
    { to: "/montador/condicoes", label: "Condições de Empresas", icon: FileSignature },
    { to: "/montador/contratos", label: "Contratos Comerciais", icon: FileSignature },
    { to: "/montador/aditivos", label: "Aditivos Contratuais", icon: FileSignature },
    { to: "/montador/comunicados", label: "Comunicados", icon: Megaphone },
  ]},
];

export default function MontadorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (user?.role === "montador" && user.aprovacao_status && user.aprovacao_status !== "aprovada") {
    return <CadastroEmAnalise />;
  }

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-aj-surface lg:grid lg:grid-cols-[260px_1fr] font-body">
      <AvaliacaoTrimestral />
      <NovaOfertaSound />
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}
          data-testid="mont-sidebar-overlay" />
      )}
      {/* Sidebar */}
      <aside className={`bg-white border-r border-aj-border flex flex-col h-screen w-[260px] max-w-[80vw]
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-200
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:static lg:translate-x-0 lg:sticky lg:top-0 lg:z-auto`}>
        <div className="px-5 py-5 border-b border-aj-border flex items-start justify-between">
          <div>
            <LogoAtivaJa size="md" />
            <div className="mt-2 inline-flex items-center gap-1.5 bg-aj-amber-soft text-aj-orange-hover text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
              <Wrench className="w-3 h-3" /> Montador
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 -mr-1 text-aj-text-muted"
            data-testid="mont-sidebar-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map((sec) => (
            <div key={sec.group} className="mb-2">
              <div className="px-5 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
                {sec.group}
              </div>
              {sec.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`mont-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2 text-sm font-medium transition-all border-l-2 ${
                      isActive
                        ? "bg-aj-amber-soft text-aj-orange-hover border-aj-orange font-semibold"
                        : "text-aj-text border-transparent hover:bg-aj-surface hover:text-aj-blue-deep"
                    }`}>
                  <n.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{n.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User card */}
        <div className="p-4 border-t border-aj-border bg-aj-surface/50">
          <button onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white transition"
            data-testid="mont-user-menu">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-aj-orange to-aj-amber text-white font-bold text-xs flex items-center justify-center">
              {(user?.name || "M").substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-xs font-semibold text-aj-blue-deep truncate">{user?.name}</div>
              <div className="text-[10px] text-aj-text-muted truncate">Montador Autônomo</div>
            </div>
            <ChevronDown className={`w-4 h-4 text-aj-text-muted transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {userMenuOpen && (
            <button onClick={handleLogout}
              className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-xs font-semibold text-aj-danger hover:bg-red-50 rounded transition"
              data-testid="mont-logout">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          )}
        </div>
      </aside>

      <main className="overflow-auto min-h-screen">
        <div className="sticky top-0 z-30 bg-white border-b border-aj-border px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 rounded-lg bg-aj-surface hover:bg-aj-amber-soft transition flex items-center justify-center flex-shrink-0"
              data-testid="mont-menu-toggle">
              <Menu className="w-5 h-5 text-aj-blue-deep" />
            </button>
            <div className="min-w-0">
              <div className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
                MONTAJÁ · MONTADOR AUTÔNOMO
              </div>
              <div className="text-sm font-display font-bold text-aj-blue-deep truncate">
                Olá, {(user?.name || "").split(" ")[0] || "Montador"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button className="hidden sm:flex w-9 h-9 rounded-lg bg-aj-surface hover:bg-aj-amber-soft transition items-center justify-center" data-testid="mont-search">
              <Search className="w-4 h-4 text-aj-text-muted" />
            </button>
            <button className="relative w-9 h-9 rounded-lg bg-aj-surface hover:bg-aj-amber-soft transition flex items-center justify-center" data-testid="mont-notif">
              <BellRing className="w-4 h-4 text-aj-text-muted" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-aj-orange" />
            </button>
          </div>
        </div>
        <AlertaNFBloqueante />
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
